import {
  useRef,
  useMemo,
  useCallback,
  useContext,
  forwardRef,
  createContext,
  ForwardedRef,
  HTMLAttributes,
  useEffect,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'

import { ModalActions } from 'primsell-shared-layer/features'
import { useClickOutside, useLockedBody } from 'primsell-shared-layer/hooks'

import * as S from './modal.styles'
import { IModalProps, ModalTypeE, IHeaderProps, IModalContext } from './modal.types'
import { WhiteCloseIcon } from 'primsell-shared-layer/assets/icons'
import { ButtonSizeT } from 'primsell-shared-layer/components/atoms/button/button.types'

const initialModalContextState: IModalContext = {
  onClose: undefined,
  showCloseButton: undefined,
  type: ModalTypeE.DEFAULT,
}

const MIN_HEIGHT_THRESHOLD = 267

export const ModalContext = createContext<IModalContext>(initialModalContextState)

const Modal = ({
  children,
  width,
  scrollable,
  closeOnClickOutside = true,
  showCloseButton = true,
  onClose,
  type = ModalTypeE.DEFAULT,
}: IModalProps) => {
  const dispatch = useDispatch()
  const modalRef = useRef<HTMLDivElement>(null)

  const handleClose = useCallback(() => {
    dispatch(ModalActions.closeModal())
    onClose && onClose()
  }, [dispatch, onClose])

  const handleClickOutside = useCallback(() => {
    if (closeOnClickOutside) {
      handleClose()
    }
  }, [closeOnClickOutside, handleClose])

  useClickOutside(modalRef, handleClickOutside)
  useLockedBody()

  const renderModalInner = useMemo(() => {
    const modalTypeConfig: Record<ModalTypeE, JSX.Element> = {
      [ModalTypeE.DEFAULT]: (
        <S.DefaultContainer ref={modalRef} width={width} scrollable={scrollable}>
          {showCloseButton && <S.CloseButton onClick={handleClose} />}
          {children}
        </S.DefaultContainer>
      ),
      [ModalTypeE.FULL_SIZE]: <S.FullSizeContainer ref={modalRef}>{children}</S.FullSizeContainer>,
    }

    return modalTypeConfig[type]
  }, [children, scrollable, showCloseButton, type, width, handleClose])

  return (
    <ModalContext.Provider value={{ type, showCloseButton, onClose: handleClose }}>
      <S.Overlay>{renderModalInner}</S.Overlay>
    </ModalContext.Provider>
  )
}

const Header = forwardRef(({ title, ...props }: IHeaderProps, ref: ForwardedRef<HTMLDivElement>) => {
  return (
    <S.Header {...props} ref={ref}>
      {title && <S.TitleTypography>{title}</S.TitleTypography>}
    </S.Header>
  )
})

const Content = forwardRef(
  ({ children, ...props }: HTMLAttributes<HTMLDivElement>, ref: ForwardedRef<HTMLDivElement>) => {
    const [height, setHeight] = useState(0)
    const [buttonSize, setButtonSize] = useState<ButtonSizeT>('sm')
    const context = useContext(ModalContext)
    const contentRef = useRef<HTMLDivElement>(null)

    const resolvedRef = ref && 'current' in ref ? ref : contentRef
    const combinedRef = resolvedRef as React.MutableRefObject<HTMLDivElement>

    useEffect(() => {
      const childElement = combinedRef.current?.querySelector('img')

      const handleImageLoad = () => {
        if (childElement instanceof HTMLImageElement) {
          const childHeight = childElement.clientHeight
          setHeight(childHeight)
          setButtonSize(childHeight < MIN_HEIGHT_THRESHOLD ? 'xxs' : 'sm')
        }
      }

      if (childElement) {
        if (childElement.complete) {
          handleImageLoad()
        } else {
          childElement.addEventListener('load', handleImageLoad)
        }
      }

      return () => {
        if (childElement) {
          childElement.removeEventListener('load', handleImageLoad)
        }
      }
    }, [children, combinedRef])

    const zoomOutButton = useMemo(() => {
      if (context.showCloseButton && context.type === ModalTypeE.FULL_SIZE) {
        return (
          <S.ZoomOutButton
            icon={<WhiteCloseIcon />}
            position={buttonSize}
            size={buttonSize}
            color="lightGrey"
            onClick={context.onClose}
          />
        )
      }
      return null
    }, [context.showCloseButton, context.type, buttonSize, context.onClose])

    return (
      children && (
        <S.Content type={context.type} {...props} ref={combinedRef}>
          {height > 0 && zoomOutButton}
          {children}
        </S.Content>
      )
    )
  }
)

const Footer = forwardRef(
  ({ children, ...props }: HTMLAttributes<HTMLDivElement>, ref: ForwardedRef<HTMLDivElement>) => {
    return (
      children && (
        <S.Footer {...props} ref={ref}>
          {children}
        </S.Footer>
      )
    )
  }
)

Header.displayName = 'Modal Header'
Content.displayName = 'Modal Content'
Footer.displayName = 'Modal Footer'

Modal.Header = Header
Modal.Content = Content
Modal.Footer = Footer

export { Modal }
